'use client'

import { useRef, useState, useEffect, useCallback } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { track } from '../lib/analytics'
import ContactModal from '../components/ContactModal'
import { hasWebGL } from '../lib/webgl'

gsap.registerPlugin(ScrollTrigger, useGSAP)

/* ─── HUD chrome (mirrors Gallery / Navigation primitives) ─── */
const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

function HudCorners({ size = 16 }: { size?: number }) {
  const c = 'var(--color-fire)'
  const b: CSSProperties = { position: 'absolute', width: size, height: size, opacity: 0.5 }
  return (
    <>
      <span aria-hidden style={{ ...b, top: 0, left: 0, borderTop: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, top: 0, right: 0, borderTop: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, left: 0, borderBottom: `1px solid ${c}`, borderLeft: `1px solid ${c}` }} />
      <span aria-hidden style={{ ...b, bottom: 0, right: 0, borderBottom: `1px solid ${c}`, borderRight: `1px solid ${c}` }} />
    </>
  )
}

// Rotating targeting reticle that frames the particle @ core.
// `locked` fires on arm hover/focus: the loose dashed rings recede and a solid
// LOCK ring converges in — the uplink has acquired the core.
function Reticle({ locked = false }: { locked?: boolean }) {
  const c = 'var(--color-fire)'
  const ease = 'var(--ease-out-quart)'
  return (
    <svg aria-hidden viewBox="0 0 100 100" className="absolute pointer-events-none" style={{ width: 'min(46vh, 30rem)', aspectRatio: '1', left: '50%', top: '50%', transform: 'translate(-50%,-50%)' }}>
      <g style={{ transformOrigin: 'center', animation: 'hud-spin 30s linear infinite', opacity: locked ? 0.25 : 1, transition: `opacity 0.4s ${ease}` }}>
        <circle cx="50" cy="50" r="48.5" fill="none" stroke={c} strokeWidth="0.35" strokeDasharray="0.4 3.6" opacity="0.5" />
        <line x1="50" y1="0.5" x2="50" y2="6" stroke={c} strokeWidth="0.7" opacity="0.8" />
        <line x1="50" y1="94" x2="50" y2="99.5" stroke={c} strokeWidth="0.7" opacity="0.8" />
        <line x1="0.5" y1="50" x2="6" y2="50" stroke={c} strokeWidth="0.7" opacity="0.8" />
        <line x1="94" y1="50" x2="99.5" y2="50" stroke={c} strokeWidth="0.7" opacity="0.8" />
      </g>
      <g style={{ transformOrigin: 'center', animation: 'hud-spin-rev 22s linear infinite', opacity: locked ? 0.2 : 1, transition: `opacity 0.4s ${ease}` }}>
        <circle cx="50" cy="50" r="41" fill="none" stroke={c} strokeWidth="0.5" strokeDasharray="16 84" opacity="0.65" />
        <circle cx="50" cy="50" r="41" fill="none" stroke={c} strokeWidth="0.5" strokeDasharray="16 84" strokeDashoffset="50" opacity="0.65" />
      </g>
      {/* LOCK ring — solid, converges from slightly oversized to seated on lock */}
      <g style={{ transformOrigin: 'center', opacity: locked ? 1 : 0, transform: locked ? 'scale(1)' : 'scale(1.07)', transition: `opacity 0.45s ${ease}, transform 0.45s ${ease}` }}>
        <circle cx="50" cy="50" r="44" fill="none" stroke={c} strokeWidth="0.7" />
        <circle cx="50" cy="50" r="37" fill="none" stroke={c} strokeWidth="0.35" opacity="0.55" />
        <line x1="50" y1="4" x2="50" y2="11" stroke={c} strokeWidth="0.8" />
        <line x1="50" y1="89" x2="50" y2="96" stroke={c} strokeWidth="0.8" />
        <line x1="4" y1="50" x2="11" y2="50" stroke={c} strokeWidth="0.8" />
        <line x1="89" y1="50" x2="96" y2="50" stroke={c} strokeWidth="0.8" />
      </g>
    </svg>
  )
}

function HexDot({ on }: { on: boolean }) {
  return <span aria-hidden style={{ width: 9, height: 10, flexShrink: 0, clipPath: HEX_DOT, background: on ? 'var(--color-fire)' : 'var(--color-steel-deep)', boxShadow: on ? '0 0 9px var(--color-fire)' : 'none', transform: on ? 'scale(1)' : 'scale(0.78)', transition: 'all .4s var(--ease-out-quart)' }} />
}

/* ─── Channel icons ─── */
const I = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'currentColor' } as const
const IconEmail = (<svg {...I}><path d="M20 4H4a2 2 0 00-2 2v12a2 2 0 002 2h16a2 2 0 002-2V6a2 2 0 00-2-2zm0 4.24l-8 4.88-8-4.88V6l8 4.88L20 6v2.24z" /></svg>)
const IconPhone = (<svg {...I}><path d="M6.62 10.79a15.5 15.5 0 006.59 6.59l2.2-2.2a1 1 0 011.02-.24 11.4 11.4 0 003.57.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.4 11.4 0 00.57 3.57 1 1 0 01-.24 1.02l-2.2 2.2z" /></svg>)
const IconGitHub = (<svg {...I}><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" /></svg>)
const IconLinkedIn = (<svg {...I}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>)
const IconFacebook = (<svg {...I}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>)
const IconInstagram = (<svg {...I}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" /></svg>)
const IconX = (<svg {...I}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>)
const IconWhatsApp = (<svg {...I}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>)

type Channel = { code: string; label: string; name: string; handle: string; href: string; icon: ReactNode; external?: boolean; copy?: string }
// Direct channels only. The "send a message" form is the PRIMARY CTA (the arm on
// the left) and is intentionally NOT duplicated as a console row here.
const CHANNELS: Channel[] = [
  { code: 'C1', label: 'EMAIL',     name: 'Email',     handle: 'abdelrahman.18036@gmail.com',   href: 'mailto:abdelrahman.18036@gmail.com',         icon: IconEmail,     copy: 'abdelrahman.18036@gmail.com' },
  { code: 'C2', label: 'CALL',      name: 'Phone',     handle: '+20 155 489 1828',              href: 'tel:+201554891828',                          icon: IconPhone,     copy: '+201554891828' },
  { code: 'C3', label: 'GITHUB',    name: 'GitHub',    handle: '@abdelrahman18036',             href: 'https://github.com/abdelrahman18036',        icon: IconGitHub,    external: true },
  { code: 'C4', label: 'LINKEDIN',  name: 'LinkedIn',  handle: 'in/abdelrahman18036',           href: 'https://www.linkedin.com/in/abdelrahman18036/', icon: IconLinkedIn, external: true },
  { code: 'C5', label: 'FACEBOOK',  name: 'Facebook',  handle: 'orange18036',                   href: 'https://www.facebook.com/orange18036/',      icon: IconFacebook,  external: true },
  { code: 'C6', label: 'INSTAGRAM', name: 'Instagram', handle: '@orange18036',                  href: 'https://www.instagram.com/orange18036/',     icon: IconInstagram, external: true },
  { code: 'C7', label: 'X',         name: 'X',         handle: '@orange18036',                  href: 'https://x.com/orange18036',                  icon: IconX,         external: true },
  { code: 'C8', label: 'WHATSAPP',  name: 'WhatsApp',  handle: '+20 155 489 1828',              href: 'https://wa.me/201554891828',                 icon: IconWhatsApp,  external: true },
]

// Email / Phone / WhatsApp are direct-contact channels (the primary "contact"
// key event); the rest fire a secondary "social_click".
const CONTACT_CODES = new Set(['C1', 'C2', 'C8'])
const channelEvent = (ch: Channel) =>
  CONTACT_CODES.has(ch.code)
    ? track('contact', { method: ch.name.toLowerCase() })
    : track('social_click', { network: ch.name.toLowerCase() })

const LBL: CSSProperties = { fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }
const LBL2: CSSProperties = { ...LBL, color: 'var(--color-stone)' }

/**
 * SCENE 08: THE PORTAL → "UPLINK"
 * The viewport-centered WebGL particle @ is the UPLINK CORE, locked in a reticle; the
 * headline + ESTABLISH UPLINK arm hug the left, the 9-channel console hugs the right.
 */
export default function Portal() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDesktop, setIsDesktop] = useState(false)
  const [focus, setFocus] = useState<number | null>(null)
  const [armHover, setArmHover] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  const [contactOpen, setContactOpen] = useState(false)
  const [contactArmed, setContactArmed] = useState(false)
  const [cairoTime, setCairoTime] = useState('')

  // Live Cairo local time beside ONLINE — data, not motion, so it runs regardless
  // of reduced-motion. Ticks once a second; interval cleared on unmount.
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Africa/Cairo', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    })
    const tick = () => setCairoTime(fmt.format(new Date()))
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [])

  // Layout switch mirrors the WebGL @ gate exactly (App.tsx): >=768px AND motion-ok.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const rm = window.matchMedia('(prefers-reduced-motion: reduce)')
    const ev = () => setIsDesktop(mq.matches && !rm.matches && hasWebGL())
    ev()
    mq.addEventListener('change', ev)
    rm.addEventListener('change', ev)
    return () => { mq.removeEventListener('change', ev); rm.removeEventListener('change', ev) }
  }, [])

  const copyTo = useCallback((raw: string, code: string) => {
    if (!navigator.clipboard) return
    navigator.clipboard.writeText(raw).then(() => {
      setCopied(code)
      setTimeout(() => setCopied(null), 1200)
    }).catch(() => {})
  }, [])

  useGSAP(
    () => {
      if (!containerRef.current) return
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
      const scope = containerRef.current
      const trig = (sel: string) => {
        const el = scope.querySelector(sel)
        return { trigger: el ?? scope, start: 'top 78%', toggleActions: 'play none none reverse' as const }
      }
      const fromTo = (selector: string, from: gsap.TweenVars, to: gsap.TweenVars) => {
        const targets = scope.querySelectorAll(selector)
        if (targets.length) gsap.fromTo(targets, from, to)
      }
      fromTo('.portal-statusbar', { y: -8, autoAlpha: 0 }, { y: 0, autoAlpha: 1, duration: 0.35, ease: 'power2.out', scrollTrigger: trig('.portal-statusbar') })
      fromTo('.portal-word', { y: 24, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: 0.06, duration: 0.5, ease: 'power4.out', scrollTrigger: trig('.portal-compose') })
      fromTo('.portal-arm', { clipPath: 'inset(0 100% 0 0)', autoAlpha: 0 }, { clipPath: 'inset(0 0 0 0)', autoAlpha: 1, duration: 0.55, ease: 'power4.out', scrollTrigger: trig('.portal-compose') })
      fromTo('.portal-core', { autoAlpha: 0, scale: 0.92 }, { autoAlpha: 1, scale: 1, duration: 0.6, ease: 'expo.out', scrollTrigger: trig('.portal-core') })
      fromTo('.portal-channels', { x: 22, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.5, ease: 'power3.out', scrollTrigger: trig('.portal-channels') })
      fromTo('.portal-chrow', { y: 10, autoAlpha: 0 }, { y: 0, autoAlpha: 1, stagger: 0.045, duration: 0.3, ease: 'power2.out', scrollTrigger: trig('.portal-channels') })
      // Idle: the ghost @ behind the channel slab breathes (replaces the old random orb).
      const ghost = scope.querySelectorAll('.portal-ghost')
      if (ghost.length) gsap.to(ghost, { opacity: 0.1, scale: 1.03, duration: 8, ease: 'sine.inOut', repeat: -1, yoyo: true })
    },
    { scope: containerRef, dependencies: [isDesktop], revertOnUpdate: true },
  )

  /* ── Pieces (shared between desktop grid and mobile stack) ── */

  const statusBar = (
    <div className="portal-statusbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', width: '100%' }}>
      <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
        <span className="font-mono uppercase" style={LBL}>08</span>
        <svg width={10} height={10} viewBox="0 0 100 100" fill="none" style={{ flexShrink: 0 }}>
          <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="none" stroke="var(--color-fire)" strokeWidth="8" />
        </svg>
        <span className="font-mono uppercase" style={LBL}>UPLINK.TERMINAL</span>
        <span className="font-mono uppercase" style={LBL2}>/ contact</span>
      </div>
      <div className="hidden sm:flex items-center gap-3" style={{ flexShrink: 0 }}>
        <span className="font-mono uppercase" style={LBL2}>{String(CHANNELS.length).padStart(2, '0')} CHANNELS</span>
        <span style={{ width: 1, height: 10, background: 'rgba(255,255,255,0.12)' }} />
        <span className="font-mono uppercase inline-flex items-center gap-1.5" style={LBL}>
          <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-fire)', animation: 'dot-pulse 2.4s ease-in-out infinite' }} />
          ONLINE
        </span>
        <span style={{ width: 1, height: 10, background: 'rgba(255,255,255,0.12)' }} />
        <span className="font-mono uppercase inline-flex items-center gap-1.5" style={LBL2}>
          CAIRO
          <span style={{ color: 'var(--color-fog)', fontVariantNumeric: 'tabular-nums' }}>{cairoTime || '--:--:--'}</span>
        </span>
        <span style={{ width: 1, height: 10, background: 'rgba(255,255,255,0.12)' }} />
        <span className="font-mono uppercase" style={{ ...LBL, color: 'var(--color-fire)' }}>LINK {String(CHANNELS.length).padStart(2, '0')}/{String(CHANNELS.length).padStart(2, '0')}</span>
      </div>
    </div>
  )

  const compose = (
    <div className="portal-compose" style={{ containerType: 'inline-size', textAlign: 'left', width: '100%' }}>
      <p className="portal-word font-mono uppercase" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: '0.6rem', letterSpacing: '0.22em', color: 'var(--color-fog)', marginBottom: 'clamp(0.85rem, 2vh, 1.25rem)' }}>
        <span aria-hidden style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-fire)', boxShadow: '0 0 9px var(--color-fire)', animation: 'dot-pulse 2.2s ease-in-out infinite', flexShrink: 0 }} />
        Available for select projects
      </p>
      <h2 style={{ marginBottom: 'clamp(1.1rem, 2.4vh, 1.6rem)' }}>
        {['LET’S', 'BUILD', 'SOMETHING'].map((w, i) => (
          <span key={i} className="portal-word font-display font-extrabold select-none" style={{ display: 'block', fontSize: 'clamp(1.6rem, 8cqi, 4rem)', lineHeight: 0.95, letterSpacing: '-0.04em', color: 'var(--color-light)' }}>{w}</span>
        ))}
        <span className="portal-word font-display font-extrabold select-none" style={{ display: 'block', fontSize: 'clamp(1.25rem, 6.1cqi, 3.3rem)', lineHeight: 1, letterSpacing: '-0.035em', color: 'var(--color-fire)', textShadow: '0 0 80px rgba(255,107,0,0.25), 0 0 160px rgba(255,107,0,0.1)' }}>EXTRAORDINARY.</span>
      </h2>
      <p className="font-mono uppercase" style={{ fontSize: '0.62rem', letterSpacing: '0.24em', color: 'var(--color-stone)', marginBottom: 'clamp(1rem, 2vh, 1.5rem)' }}>
        {'// Ready to transmit'}
        <span aria-hidden style={{ display: 'inline-block', width: '0.5ch', height: '1em', background: 'var(--color-fire)', marginLeft: 5, verticalAlign: '-0.12em', animation: 'blink 1s step-end infinite' }} />
      </p>
      <button
        type="button"
        className="portal-arm"
        data-cursor-label="Open form"
        onClick={() => { track('contact', { method: 'form_open', source: 'uplink' }); setContactArmed(true); setContactOpen(true) }}
        onMouseEnter={() => setArmHover(true)}
        onMouseLeave={() => setArmHover(false)}
        onFocus={() => setArmHover(true)}
        onBlur={() => setArmHover(false)}
        style={{ position: 'relative', display: 'block', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 'clamp(0.9rem, 2vh, 1.2rem) clamp(1.25rem, 2vw, 1.75rem)', maxWidth: '26rem', overflow: 'hidden' }}
      >
        <HudCorners size={14} />
        <span aria-hidden style={{ position: 'absolute', inset: 0, background: 'var(--color-fire)', opacity: 0.12, transformOrigin: 'left', transform: armHover ? 'scaleX(1)' : 'scaleX(0)', transition: 'transform 0.45s var(--ease-out-quart)', zIndex: 0 }} />
        <span style={{ position: 'relative', zIndex: 1, display: 'block' }}>
          <span className="font-display font-bold" style={{ display: 'block', fontSize: 'clamp(0.95rem, 1.4vw, 1.15rem)', letterSpacing: '0.04em', color: 'var(--color-fire)' }}>◉ ESTABLISH UPLINK ▸</span>
          <span className="font-mono uppercase" style={{ display: 'block', marginTop: 6, fontSize: '0.55rem', letterSpacing: '0.22em', color: 'var(--color-stone)' }}>PRIMARY CHANNEL // SECURE FORM ▸</span>
        </span>
      </button>
    </div>
  )

  const channels = (
    <div className="portal-channels" style={{ position: 'relative', width: '100%', padding: 'clamp(1.25rem, 2.5vw, 1.75rem) clamp(1.25rem, 2vw, 1.5rem)' }}>
      <HudCorners size={16} />
      <span className="portal-ghost font-display" aria-hidden style={{ position: 'absolute', right: '0.4rem', top: '50%', transform: 'translateY(-50%)', fontSize: 'clamp(6rem, 11vw, 11rem)', fontWeight: 800, color: 'var(--color-fire)', opacity: 0.06, pointerEvents: 'none', lineHeight: 0.8, zIndex: 0 }}>@</span>
      <div className="font-mono uppercase" style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', letterSpacing: '0.24em', color: 'var(--color-text-tertiary)', marginBottom: 'clamp(0.6rem, 1.5vh, 1rem)' }}>
        <span>{'// Channels'}</span>
        <span>Resolved · {String(CHANNELS.length).padStart(2, '0')}/{String(CHANNELS.length).padStart(2, '0')}</span>
      </div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {CHANNELS.map((ch, i) => {
          const on = focus === i
          return (
            <a
              key={ch.code}
              className="portal-chrow"
              href={ch.href}
              {...(ch.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              data-cursor-label={ch.label}
              onClick={() => channelEvent(ch)}
              onMouseEnter={() => setFocus(i)}
              onMouseLeave={() => setFocus(null)}
              onFocus={() => setFocus(i)}
              onBlur={() => setFocus(null)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', textDecoration: 'none', padding: 'clamp(0.5rem, 1.3vh, 0.8rem) 0', borderBottom: i < CHANNELS.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none', transform: on ? 'translateX(4px)' : 'none', transition: 'transform 0.35s var(--ease-out-quart)' }}
            >
              <HexDot on={on} />
              <span className="font-mono" style={{ fontSize: '0.58rem', letterSpacing: '0.1em', color: on ? 'var(--color-fire)' : 'var(--color-text-tertiary)', width: '2.4ch', flexShrink: 0, transition: 'color 0.3s' }}>{ch.code}</span>
              <span aria-hidden style={{ color: on ? 'var(--color-fire)' : 'var(--color-stone)', flexShrink: 0, display: 'flex', transition: 'color 0.3s' }}>{ch.icon}</span>
              <span className="font-display font-bold" style={{ fontSize: 'clamp(0.95rem, 1.1vw, 1.2rem)', letterSpacing: '-0.01em', color: on ? 'var(--color-light)' : 'var(--color-mist)', flexShrink: 0, transition: 'color 0.3s' }}>{ch.name}</span>
              <span className="font-mono" style={{ marginLeft: 'auto', minWidth: 0, fontSize: '0.58rem', letterSpacing: '0.06em', color: on ? 'var(--color-fog)' : 'var(--color-stone)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', transition: 'color 0.3s' }}>{ch.handle}</span>
              {ch.copy && (
                <button
                  type="button"
                  aria-label={ch.name === 'Phone' ? 'Copy phone number' : 'Copy email address'}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); copyTo(ch.copy as string, ch.code) }}
                  style={{ flexShrink: 0, fontSize: '0.55rem', letterSpacing: '0.1em', color: copied === ch.code ? 'var(--color-fire)' : 'var(--color-stone)', padding: '6px 6px', minWidth: 24, minHeight: 24, transition: 'color 0.3s' }}
                >
                  {copied === ch.code ? 'COPIED' : '⧉'}
                </button>
              )}
              <span className="font-mono" aria-hidden style={{ flexShrink: 0, color: 'var(--color-fire)', opacity: on ? 1 : 0, transform: on ? 'translateX(0)' : 'translateX(-4px)', transition: 'opacity 0.3s, transform 0.3s' }}>▸</span>
            </a>
          )
        })}
      </div>
    </div>
  )

  const footer = (
    <div className="portal-foot font-mono uppercase" style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', fontSize: '0.55rem', letterSpacing: '0.18em', color: 'var(--color-text-tertiary)', width: '100%' }}>
      <span style={{ color: 'var(--color-stone)' }}>CORE: @ // address resolved</span>
      <span style={{ color: 'var(--color-ash)' }}>·</span>
      <span>LAT 30.0444</span>
      <span style={{ color: 'var(--color-ash)' }}>·</span>
      <span>LNG 31.2357</span>
      <span style={{ color: 'var(--color-ash)' }}>·</span>
      <span>Cairo</span>
    </div>
  )

  const desktopCore = (
    <>
      <HudCorners size={18} />
      <Reticle locked={armHover} />
      <span className="font-mono uppercase" aria-hidden style={{ position: 'absolute', top: '7%', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', letterSpacing: '0.26em', color: armHover ? 'var(--color-fire)' : 'var(--color-stone)', whiteSpace: 'nowrap', transition: 'color 0.3s var(--ease-out-quart)' }}>{armHover ? '◉ LOCK ACQUIRED' : '◇ UPLINK.CORE'}</span>
      <span className="font-mono uppercase" aria-hidden style={{ position: 'absolute', bottom: '7%', left: '50%', transform: 'translateX(-50%)', fontSize: '0.6rem', letterSpacing: '0.26em', color: 'var(--color-fog)', whiteSpace: 'nowrap' }}>@ // address resolved</span>
      <span aria-hidden style={{ position: 'absolute', left: '12%', right: '12%', height: 1, background: 'linear-gradient(90deg, transparent, var(--color-fire), transparent)', opacity: 0, animation: 'hud-scan 6s linear infinite' }} />
      {/* bearing line — routes the focused channel toward the @ core */}
      <span aria-hidden style={{ position: 'absolute', top: '50%', right: 0, width: '46%', height: 1, background: 'linear-gradient(90deg, transparent, var(--color-fire))', opacity: focus !== null ? 0.7 : 0, transform: focus !== null ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'right', transition: 'opacity 0.4s var(--ease-out-quart), transform 0.4s var(--ease-out-quart)' }} />
      {/* intent beam — mirrors the bearing line, fires from the arm (left) INTO the core on arm hover/focus */}
      <span aria-hidden style={{ position: 'absolute', top: '50%', left: 0, width: '46%', height: 1, background: 'linear-gradient(90deg, var(--color-fire), transparent)', opacity: armHover ? 0.8 : 0, transform: armHover ? 'scaleX(1)' : 'scaleX(0)', transformOrigin: 'left', transition: 'opacity 0.4s var(--ease-out-quart), transform 0.4s var(--ease-out-quart)' }} />
    </>
  )

  const mobileCore = (
    <div aria-hidden style={{ position: 'relative', width: 128, height: 128, alignSelf: 'center', flexShrink: 0, display: 'grid', placeItems: 'center' }}>
      <svg viewBox="0 0 100 100" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
        {/* rings ROTATE on mobile too — the living reticle is the scene's identity */}
        <g style={{ transformOrigin: 'center', animation: 'hud-spin 28s linear infinite' }}>
          <circle cx="50" cy="50" r="47" fill="none" stroke="var(--color-fire)" strokeWidth="0.6" strokeDasharray="2 4" opacity="0.5" />
        </g>
        <g style={{ transformOrigin: 'center', animation: 'hud-spin-rev 20s linear infinite' }}>
          <circle cx="50" cy="50" r="38" fill="none" stroke="var(--color-fire)" strokeWidth="0.5" strokeDasharray="14 86" opacity="0.6" />
        </g>
      </svg>
      <span className="font-display font-bold" style={{ fontSize: '2.6rem', color: 'var(--color-fire)', textShadow: '0 0 30px rgba(255,107,0,0.4)' }}>@</span>
      <span style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: 7, height: 8, clipPath: HEX_DOT, background: 'var(--color-fire)', boxShadow: '0 0 9px var(--color-fire)', animation: 'dot-pulse 2.4s ease-in-out infinite' }} />
    </div>
  )

  return (
    <section
      ref={containerRef}
      id="portal"
      data-scene="portal"
      className="relative w-full overflow-hidden"
      style={{ backgroundColor: 'var(--color-abyss)', minHeight: '100vh' }}
    >
      {isDesktop ? (
        <div
          className="relative"
          style={{
            zIndex: 5, minHeight: '100vh', display: 'grid',
            // Symmetric side columns so the core column (and its reticle) centre on the
            // viewport exactly where the WebGL @ core sits — no left/right drift.
            gridTemplateColumns: 'minmax(320px, 31vw) 1fr minmax(320px, 31vw)',
            gridTemplateRows: 'auto 1fr auto',
            columnGap: 'var(--gutter)', rowGap: 'clamp(1.5rem, 3vh, 2.5rem)',
            padding: 'clamp(5rem, 9vh, 7rem) var(--gutter) clamp(3rem, 6vh, 4.5rem)',
          }}
        >
          <div style={{ gridColumn: '1 / -1', gridRow: 1 }}>{statusBar}</div>
          <div style={{ gridColumn: 1, gridRow: 2, alignSelf: 'center' }}>{compose}</div>
          <div className="portal-core" style={{ gridColumn: 2, gridRow: 2, position: 'relative', alignSelf: 'stretch', pointerEvents: 'none' }}>{desktopCore}</div>
          <div style={{ gridColumn: 3, gridRow: 2, alignSelf: 'center' }}>{channels}</div>
          <div style={{ gridColumn: '1 / -1', gridRow: 3, alignSelf: 'end' }}>{footer}</div>
        </div>
      ) : (
        <div
          className="relative flex flex-col"
          style={{ zIndex: 5, minHeight: '100vh', gap: 'clamp(1.75rem, 5vh, 2.75rem)', padding: 'clamp(5.5rem, 11vh, 7.5rem) clamp(1.25rem, 5vw, 2rem) clamp(3rem, 6vh, 4rem)' }}
        >
          {statusBar}
          {mobileCore}
          {compose}
          {channels}
          {footer}
        </div>
      )}

      {/* thin fire divider */}
      <div aria-hidden style={{ position: 'absolute', bottom: 0, left: 'var(--gutter)', right: 'var(--gutter)', height: 1, background: 'rgba(255,107,0,0.15)', zIndex: 5 }} />

      {/* contact form popup — armed=true enters scaling out of the reticle/core center */}
      <ContactModal open={contactOpen} armed={contactArmed} onClose={() => { setContactOpen(false); setContactArmed(false) }} />
    </section>
  )
}
